import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { foundation } from '../../ui/tokens';
import { C, AUTH_MOTION } from './loginAuthTheme';
import { useIsStepGhost } from './stepGhost';

/**
 * AuthFieldMessage — the ONE home for a pre-auth form's attempt result
 * (login "Option E", 2026-09-24). Renders under the field it is about and
 * never inserts into the layout abruptly:
 *
 *   open   → height 0 → content over 280ms; text fades in (180ms, +60ms)
 *   close  → text fades out (120ms) THEN height collapses (280ms)
 *   swap   → old text fades (100ms), new text fades in, height re-tweens
 *
 * Timer-driven only (no transitionend / animationend — iOS PWA drops them).
 * Reduced motion: every duration is 0; the layout result is identical.
 * The live region is always mounted (stable role) so screen readers announce changes.
 * Height open/close share the shared pre-auth curve (`AUTH_MOTION.ease`,
 * 2026-09-26 smoothness pass) — durations under 240ms were raised to 280ms
 * so the curve has room to settle without a snap.
 */

const { color, fontSize, fontFamily, fontWeight } = foundation;
const EASE = AUTH_MOTION.ease;
const T = { openH: 280, textIn: 180, textDelay: 60, closeText: 120, closeH: 280, swapOut: 100 };

const TONE = {
  danger: color.semantic.danger,
  warning: C.warningText,
  info: color.neutral.textSecondary,
};

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Icon({ kind }) {
  const common = {
    width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 2.5, strokeLinecap: 'round', 'aria-hidden': true,
    style: { flex: '0 0 auto', marginTop: 2 },
  };
  if (kind === 'clock') return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
  if (kind === 'offline') return <svg {...common}><path d="M2 8.5a15 15 0 0 1 20 0M5.5 12a10 10 0 0 1 13 0M9 15.5a5 5 0 0 1 6 0M12 19v.01" /><path d="M3 3l18 18" /></svg>;
  return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" /></svg>;
}
Icon.propTypes = { kind: PropTypes.string };

export default function AuthFieldMessage({
  message, tone = 'danger', icon = 'alert', id, testId, align = 'start',
}) {
  const reduce = prefersReducedMotion();
  const d = (k) => (reduce ? 0 : T[k]);
  const innerRef = useRef(null);
  const timers = useRef([]);
  // `shown` is what is painted; it lags `message` so a closing line keeps its
  // text while it fades out.
  // A message present at MOUNT (a late mount) still opens with the height
  // tween: start closed and let the layout effect below run the OPEN branch.
  // Reduced motion paints it open at once (all durations are 0 anyway).
  // A StepCrossfade leaving-snapshot ghost is a frozen picture of the old step: it
  // paints open at once (an opening ghost would shift its own content).
  const ghost = useIsStepGhost();
  const startOpen = Boolean(message) && (reduce || ghost);
  const [shown, setShown] = useState(startOpen ? { message, tone, icon } : null);
  const [height, setHeight] = useState(startOpen ? 'auto' : 0);
  const [opacity, setOpacity] = useState(startOpen ? 1 : 0);
  const [hTransition, setHTransition] = useState('none');
  const wrapRef = useRef(null);
  const prevRef = useRef(startOpen ? { message, tone, icon } : null);
  // True while a message is (or is becoming) visible — false once a close
  // has started, so a re-open mid-close grows from where it is, not from 0.
  const openRef = useRef(startOpen);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => clearTimers, []);

  useLayoutEffect(() => {
    const prev = prevRef.current;
    const next = message ? { message, tone, icon } : null;
    prevRef.current = next;
    const same = prev && next
      && prev.message === next.message && prev.tone === next.tone && prev.icon === next.icon;
    if ((!prev && !next) || same) return;
    clearTimers();
    const measure = () => innerRef.current?.scrollHeight ?? 0;
    const currentPx = () => wrapRef.current?.offsetHeight ?? 0;
    // Reading offsetHeight forces the browser to lay out the committed px
    // height, so the following height change is a real transition (not a
    // jump from 'auto').
    const reflow = () => { void wrapRef.current?.offsetHeight; };
    // Pin the wrapper at a px height WITHOUT a transition and COMMIT it before
    // the tween starts. A plain setState pair let the next 0ms timer batch the
    // pin and the tween into ONE commit (auto -> 0 with a transition = an
    // instant snap on close; real-build frame logs, 2026-10-21).
    const pin = (px) => flushSync(() => { setHTransition('none'); setHeight(px); });

    if (next && !openRef.current) {
      // OPEN — from the current height (0, or wherever a close left it)
      openRef.current = true;
      const from = currentPx();
      // (inside a layout effect: its setStates commit synchronously, no flushSync needed)
      setShown(next);
      setOpacity(0);
      setHTransition('none');
      setHeight(from);
      later(() => {
        reflow();
        setHTransition(`height ${d('openH')}ms ${EASE}`);
        setHeight(measure());
      }, 0);
      later(() => setOpacity(1), d('textDelay'));
      later(() => { setHTransition('none'); setHeight('auto'); }, d('openH') + 20);
    } else if (!next) {
      // CLOSE — fade first, then collapse
      openRef.current = false;
      setOpacity(0);
      later(() => {
        pin(currentPx());
        reflow();
        setHTransition(`height ${d('closeH')}ms ${EASE}`);
        setHeight(0);
        later(() => setShown(null), d('closeH') + 20);
      }, d('closeText'));
    } else {
      // SWAP in place (message, tone or icon changed while open)
      setOpacity(0);
      later(() => {
        const from = currentPx();
        flushSync(() => { setShown(next); setHTransition('none'); setHeight(from); });
        reflow();
        setHTransition(`height ${d('openH')}ms ${EASE}`);
        setHeight(measure());
        setOpacity(1);
        later(() => { setHTransition('none'); setHeight('auto'); }, d('openH') + 20);
      }, d('swapOut'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, tone, icon]);

  const toneColor = TONE[shown?.tone] || TONE.danger;

  return (
    <div
      ref={wrapRef}
      data-testid={testId ? `${testId}-wrap` : undefined}
      style={{ height, overflow: 'hidden', transition: hTransition }}
    >
      <div
        ref={innerRef}
        id={id}
        // ONE stable live region for every tone: a region whose role flips in
        // the same commit that inserts text is dropped by VoiceOver. The
        // field itself carries aria-invalid + aria-describedby for errors.
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid={testId}
        style={{
          paddingTop: shown ? 8 : 0,
          display: 'flex',
          gap: 6,
          alignItems: 'flex-start',
          justifyContent: align === 'center' ? 'center' : 'flex-start',
          textAlign: align === 'center' ? 'center' : 'left',
          fontSize: fontSize.sm,
          fontFamily: fontFamily.sans,
          fontWeight: fontWeight.medium,
          lineHeight: 1.45,
          color: toneColor,
          opacity,
          transition: `opacity ${opacity ? d('textIn') : d('closeText')}ms ease`,
        }}
      >
        {shown ? (
          <>
            <Icon kind={shown.icon} />
            <span>{shown.message}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}

AuthFieldMessage.propTypes = {
  message: PropTypes.node,
  tone: PropTypes.oneOf(['danger', 'warning', 'info']),
  icon: PropTypes.oneOf(['alert', 'clock', 'offline']),
  id: PropTypes.string,
  testId: PropTypes.string,
  align: PropTypes.oneOf(['start', 'center']),
};
